import React, { useState, useEffect, useMemo } from "react";
import {
    Box, TextField, Button, Typography, Divider, Card,
    Grid, List, ListItem, ListItemText, ListItemButton, Paper, CircularProgress
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy } from "firebase/firestore";

function CustomerwiseReport() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [allTrips, setAllTrips] = useState([]);
    const [allPayments, setAllPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);

    const fetchBaseData = async () => {
        setLoading(true);
        try {
            const start = Timestamp.fromDate(new Date(fromDate + " 00:00:00"));
            const end = Timestamp.fromDate(new Date(toDate + " 23:59:59"));

            const qTrips = query(
                collection(db, "trips"),
                where("grossDate", ">=", start),
                where("grossDate", "<=", end),
                orderBy("grossDate", "asc")
            );

            const qPay = query(
                collection(db, "customer_history"),
                where("date", ">=", start),
                where("date", "<=", end),
                orderBy("date", "asc")
            );

            const [tripSnap, paySnap] = await Promise.all([getDocs(qTrips), getDocs(qPay)]);

            const trips = tripSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                calculatedTotal: (doc.data().products || []).reduce((acc, p) => acc + (Number(p.amount) || 0), 0)
            }));

            const payments = paySnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setAllTrips(trips);
            setAllPayments(payments);

            if (trips.length > 0 && !selectedCustomerId) {
                const sorted = [...new Set(trips.map(t => t.customerId))];
                setSelectedCustomerId(sorted[0]);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const customerList = useMemo(() => {
        const custMap = {};
        allTrips.forEach(t => {
            if (t.customerId && !custMap[t.customerId]) {
                custMap[t.customerId] = t.customerName || "Unknown Customer";
            }
        });
        return Object.entries(custMap)
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allTrips]);

    const reportData = useMemo(() => {
        if (!selectedCustomerId) return { trips: [], payments: [], products: [] };
        const filteredTrips = allTrips.filter(t => t.customerId === selectedCustomerId);
        const filteredPayments = allPayments.filter(p => p.customerId === selectedCustomerId);
        return {
            trips: filteredTrips,
            payments: filteredPayments,
            customerName: filteredTrips[0]?.customerName || "N/A"
        };
    }, [selectedCustomerId, allTrips, allPayments]);

    useEffect(() => { fetchBaseData(); }, []);

    const cellStyle = { border: '1px solid #000', padding: '6px', fontSize: '0.8rem' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2', textAlign: 'center' };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f5f5f5' }}>
            {/* Top Bar */}
            <Paper elevation={1} sx={{ p: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }} className="no-print">
                <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
                <Button variant="contained" onClick={fetchBaseData} disabled={loading}>
                    {loading ? "..." : "Load Data"}
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => window.print()}>Print PDF</Button>
            </Paper>

            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Left Sidebar */}
                <Paper sx={{ width: 260, mr: 1, display: 'flex', flexDirection: 'column' }} className="no-print">
                    <Typography variant="button" sx={{ p: 2, bgcolor: '#444', color: '#fff' }}>Customer List</Typography>
                    <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        {customerList.map((cust) => (
                            <ListItemButton
                                key={cust.id}
                                selected={selectedCustomerId === cust.id}
                                onClick={() => setSelectedCustomerId(cust.id)}
                            >
                                {/* <p>{cust.id}</p> */}
                                <ListItemText primary={cust.name} primaryTypographyProps={{ fontSize: '0.8rem' }} />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>

                {/* Report Content */}
                <Box sx={{ flexGrow: 1, bgcolor: '#fff', p: "20px", overflowY: 'auto' }} className="printable-area">
                    {selectedCustomerId ? (
                        <Box sx={{ width: '100%' }}>
                            <Box textAlign="center" mb={2}>
                                <Typography variant="h5" fontWeight="bold">MATOSHRI INFRA DHARASHIV PVT LTD</Typography>
                                <Typography variant="body2">Sarola, Dist. Dharashiv, Maharashtra</Typography>
                                <Divider sx={{ my: 2, borderBottomWidth: 2, borderColor: '#000' }} />
                                <Typography variant="h6" fontWeight="bold">CUSTOMER STATEMENT: {reportData.customerName.toUpperCase()}</Typography>
                                <Typography variant="body2">Date Range: {fromDate} to {toDate}</Typography>
                            </Box>

                            {/* Section I */}
                            {/* Section I */}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>I. TRIP SALES LOG</Typography>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerStyle, width: '40px' }}>Sr.</th>
                                        <th style={headerStyle}>Ticket</th>
                                        <th style={headerStyle}>Date</th>
                                        <th style={headerStyle}>Chalan No</th>
                                        <th style={headerStyle}>Vehicle</th>
                                        {/* SEPARATED COLUMNS START */}
                                        <th style={{ ...headerStyle, textAlign: 'left' }}>Product Description</th>
                                        <th style={headerStyle}>Qty</th>
                                        {/* SEPARATED COLUMNS END */}
                                        <th style={headerStyle}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.trips.map((row, index) => (
                                        <tr key={row.id}>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.ticketNo}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {row.grossDate?.toDate().toLocaleDateString('en-GB')}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.challan}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.vehicleNo}</td>

                                            {/* PRODUCT COLUMN */}
                                            <td style={{ ...cellStyle, textAlign: 'left' }}>
                                                {row.products?.map((p, i) => (
                                                    <div key={i} style={{ borderBottom: i < row.products.length - 1 ? '1px solid #eee' : 'none' }}>
                                                        {p.productName}
                                                    </div>
                                                ))}
                                            </td>

                                            {/* QTY COLUMN */}
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {row.products?.map((p, i) => (
                                                    <div key={i} style={{ borderBottom: i < row.products.length - 1 ? '1px solid #eee' : 'none', fontWeight: '500' }}>
                                                        {p.qty || p.brass || 0}
                                                    </div>
                                                ))}
                                            </td>

                                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                                ₹{row.calculatedTotal.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* TOTAL FOOTER */}
                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                        <td colSpan={7} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>TOTAL SALES</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                            ₹{reportData.trips.reduce((acc, curr) => acc + curr.calculatedTotal, 0).toLocaleString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Section II */}
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>II. COLLECTION LOG</Typography>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerStyle, width: '40px' }}>Sr.</th>
                                        <th style={headerStyle}>Date</th>
                                        <th style={headerStyle}>Mode</th>
                                        <th style={headerStyle}>Note</th>
                                        <th style={headerStyle}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.payments.map((row, index) => (
                                        <tr key={row.id}>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.date?.toDate().toLocaleDateString('en-GB')}</td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.paymentMode}</td>
                                            <td style={cellStyle}>{row.note || "-"}</td>
                                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>₹{(Number(row.amount) || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                                        <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>TOTAL RECEIVED</td>
                                        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                            ₹{reportData.payments.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0).toLocaleString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                    ) : (
                        <Typography variant="body1" textAlign="center" mt={10}>Please select a customer from the sidebar.</Typography>
                    )}
                </Box>
            </Box>

            <style>{`
                @media print {
                    @page { size: A4; margin: 15mm; }
                    .no-print { display: none !important; }
                    body { background: none; padding: 0; margin: 0; }
                    .printable-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 100%; 
                        padding: 0;
                        visibility: visible;
                        padding:20px;
                    }
                    * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                }
            `}</style>
        </Box>
    );
}

export default CustomerwiseReport;